import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
	Box,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import { useCallback, useState } from "react";
import {
	EDIT_ICON_SX,
	TEXT,
	tableRowsSx,
} from "@/controls/section-panel/SectionPanel";
import { WriteLockTooltip } from "@/shared/components/WriteLockTooltip";
import { OPERATIONAL_WRITES_LOCKED_MESSAGE } from "@/shared/hooks/useOperationalWritesLocked";
import type { CampaignData } from "../models/CampaignData";
import { COLORS } from "../models/constants";

interface ProductionTableProps {
	campaign: CampaignData | null;
	onUpdateBillets?: (value: number) => void;
	onUpdateWeight?: (value: number) => void;
	onResetTemporal?: () => void;
	writeDisabled?: boolean;
}

const cellSx = {
	py: 0,
	px: 1.5,
	height: 28,
	whiteSpace: "nowrap" as const,
	...TEXT.body,
};

const headerCellSx = {
	...cellSx,
	height: 28,
	...TEXT.head,
	color: "text.secondary",
};

const labelCellSx = {
	...cellSx,
	...TEXT.label,
	color: "text.primary",
};

const valueCellSx = {
	...cellSx,
	textAlign: "right" as const,
	fontVariantNumeric: "tabular-nums",
};

export const ProductionTable = ({
	campaign,
	onUpdateBillets,
	onUpdateWeight,
	onResetTemporal,
	writeDisabled = false,
}: ProductionTableProps) => {
	const [editingField, setEditingField] = useState<"billets" | "weight" | null>(
		null,
	);
	const [editValue, setEditValue] = useState("");

	const scheduledBillets = campaign?.ScheduledBilletsCount ?? 0;
	const scheduledWeight = campaign?.ScheduledWeight ?? 0;
	const dischBillets = campaign?.DischargedBillets ?? 0;
	const dischWeight = campaign?.DischargedWeight ?? 0;
	const prodBillets = campaign?.ProductionBillets ?? 0;
	const remainBillets = scheduledBillets - prodBillets;
	const remainWeight = scheduledWeight - dischWeight;
	const temporal = campaign?.TemporalBillets ?? 0;

	const handleStartEdit = useCallback(
		(field: "billets" | "weight") => {
			setEditingField(field);
			setEditValue(
				field === "billets"
					? String(scheduledBillets)
					: String(scheduledWeight),
			);
		},
		[scheduledBillets, scheduledWeight],
	);

	const handleBlur = useCallback(() => {
		if (editingField === "billets") {
			const val = Number.parseInt(editValue, 10);
			if (!Number.isNaN(val) && val !== scheduledBillets) {
				onUpdateBillets?.(val);
			}
		} else if (editingField === "weight") {
			const val = Number.parseFloat(editValue);
			if (!Number.isNaN(val) && val !== scheduledWeight) {
				onUpdateWeight?.(val);
			}
		}
		setEditingField(null);
	}, [
		editingField,
		editValue,
		scheduledBillets,
		scheduledWeight,
		onUpdateBillets,
		onUpdateWeight,
	]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter") handleBlur();
			if (e.key === "Escape") setEditingField(null);
		},
		[handleBlur],
	);

	const renderEditable = (
		field: "billets" | "weight",
		value: number,
		format: (v: number) => string,
	) => {
		// Fixed-size wrapper so editing doesn't shift the layout
		const wrapper = {
			height: 28,
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-end",
		} as const;

		if (editingField === field) {
			return (
				<Box sx={wrapper}>
					<TextField
						type="number"
						value={editValue}
						onChange={(e) => setEditValue(e.target.value)}
						onBlur={handleBlur}
						onKeyDown={handleKeyDown}
						size="small"
						autoFocus
						variant="standard"
						slotProps={{
							input: { disableUnderline: true },
						}}
						sx={{
							"& input": {
								fontSize: TEXT.body.fontSize,
								fontVariantNumeric: "tabular-nums",
								textAlign: "right",
								p: 0,
								height: 28,
								lineHeight: "28px",
							},
							"& input::-webkit-inner-spin-button, & input::-webkit-outer-spin-button":
								{ opacity: 1, height: 18 },
							width: "100%",
						}}
					/>
				</Box>
			);
		}
		if (writeDisabled) {
			return (
				<Tooltip title={OPERATIONAL_WRITES_LOCKED_MESSAGE}>
					<Box sx={{ ...wrapper, gap: 0.5 }}>
						{format(value)}
						<EditOutlinedIcon
							sx={{ ...EDIT_ICON_SX, color: "action.disabled" }}
						/>
					</Box>
				</Tooltip>
			);
		}
		return (
			<Box
				onClick={() => handleStartEdit(field)}
				sx={{
					...wrapper,
					cursor: "pointer",
					gap: 0.5,
					"&:hover": { textDecoration: "underline" },
				}}
			>
				{format(value)}
				<EditOutlinedIcon sx={EDIT_ICON_SX} />
			</Box>
		);
	};

	const remainingOutOfRange = (val: number) =>
		val < 0 ? { color: COLORS.outOfRange, fontWeight: "bold" } : {};

	return (
		<Table size="small" sx={{ tableLayout: "fixed", ...tableRowsSx }}>
			<colgroup>
				<col style={{ width: "40%" }} />
				<col style={{ width: "30%" }} />
				<col style={{ width: "30%" }} />
			</colgroup>
			<TableHead>
				<TableRow>
					<TableCell sx={headerCellSx} />
					<TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
						Tons
					</TableCell>
					<TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
						Billets
					</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{/* Scheduled (editable) */}
				<TableRow>
					<TableCell sx={labelCellSx}>Scheduled Prod</TableCell>
					<TableCell sx={valueCellSx}>
						{renderEditable("weight", scheduledWeight, (v) => v.toFixed(2))}
					</TableCell>
					<TableCell sx={valueCellSx}>
						{renderEditable("billets", scheduledBillets, (v) => String(v))}
					</TableCell>
				</TableRow>

				{/* Discharged */}
				<TableRow>
					<TableCell sx={labelCellSx}>Discharged Prod</TableCell>
					<TableCell sx={valueCellSx}>{dischWeight.toFixed(2)}</TableCell>
					<TableCell sx={valueCellSx}>{dischBillets}</TableCell>
				</TableRow>

				{/* Remaining */}
				<TableRow>
					<TableCell sx={labelCellSx}>Remaining Prod</TableCell>
					<TableCell
						sx={{ ...valueCellSx, ...remainingOutOfRange(remainWeight) }}
					>
						{remainWeight.toFixed(2)}
					</TableCell>
					<TableCell
						sx={{ ...valueCellSx, ...remainingOutOfRange(remainBillets) }}
					>
						{remainBillets}
					</TableCell>
				</TableRow>

				{/* Temporary */}
				<TableRow>
					<TableCell sx={labelCellSx}>Temporary</TableCell>
					<TableCell colSpan={2} sx={valueCellSx}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								gap: 0.5,
							}}
						>
							{temporal}
							<WriteLockTooltip locked={writeDisabled}>
								<IconButton
									size="small"
									onClick={onResetTemporal}
									disabled={writeDisabled}
									sx={{ p: 0 }}
									title="Reset temporary billet count"
								>
									<RestartAltIcon sx={{ fontSize: 15 }} />
								</IconButton>
							</WriteLockTooltip>
						</Box>
					</TableCell>
				</TableRow>
			</TableBody>
		</Table>
	);
};
